// Vanilla JS Combobox - Simple alternative to SlimSelect for basic selects
// No dependencies, pure vanilla JavaScript

class VanillaCombobox {
  constructor(selectElement, options = {}) {
    this.select = selectElement
    this.options = {
      placeholder: options.placeholder || 'Sélectionner...',
      searchable: options.searchable !== false, // Default: true
      allowClear: options.allowClear !== false, // Default: true
      ...options
    }
    this.isOpen = false
    this.filteredOptions = []
    this.init()
  }

  init() {
    // Skip if already initialized
    if (this.select._vanillaCombobox) return
    this.select._vanillaCombobox = this

    // Create wrapper
    this.wrapper = document.createElement('div')
    this.wrapper.className = 'vanilla-combobox-wrapper'
    // Insert wrapper after the select (so it appears after label in the flow)
    // Then move select inside wrapper
    this.select.parentNode.insertBefore(this.wrapper, this.select.nextSibling)
    this.wrapper.appendChild(this.select)

    // Hide native select
    this.select.style.display = 'none'

    // Create combobox elements
    this.createCombobox()

    // Populate options
    this.updateOptions()

    // Bind events
    this.bindEvents()
  }

  createCombobox() {
    // Container
    this.container = document.createElement('div')
    this.container.className = 'vanilla-combobox'
    this.wrapper.appendChild(this.container)

    // Selected value display
    this.display = document.createElement('div')
    this.display.className = 'vanilla-combobox-display'
    this.display.setAttribute('role', 'combobox')
    this.display.setAttribute('aria-expanded', 'false')
    this.display.setAttribute('aria-haspopup', 'listbox')
    this.container.appendChild(this.display)

    // Dropdown arrow
    const arrow = document.createElement('span')
    arrow.className = 'vanilla-combobox-arrow'
    arrow.innerHTML = '▼'
    this.display.appendChild(arrow)

    // Dropdown
    this.dropdown = document.createElement('div')
    this.dropdown.className = 'vanilla-combobox-dropdown'
    this.dropdown.setAttribute('role', 'listbox')
    this.container.appendChild(this.dropdown)

    // Search input (if searchable)
    if (this.options.searchable) {
      this.searchInput = document.createElement('input')
      this.searchInput.type = 'text'
      this.searchInput.className = 'vanilla-combobox-search'
      this.searchInput.placeholder = 'Rechercher...'
      this.searchInput.setAttribute('aria-label', 'Rechercher')
      this.dropdown.appendChild(this.searchInput)
    }

    // Options container
    this.optionsContainer = document.createElement('div')
    this.optionsContainer.className = 'vanilla-combobox-options'
    this.dropdown.appendChild(this.optionsContainer)
  }

  updateOptions() {
    const selectedValue = this.select.value
    const options = Array.from(this.select.options)

    // Update display
    const selectedOption = options.find(opt => opt.value === selectedValue)
    if (selectedOption && selectedValue) {
      this.display.textContent = selectedOption.text
      this.display.classList.add('has-value')
    } else {
      this.display.textContent = this.options.placeholder
      this.display.classList.remove('has-value')
    }

    // Store all options
    this.allOptions = options.map(opt => ({
      value: opt.value,
      text: opt.text,
      disabled: opt.disabled,
      selected: opt.selected
    }))

    this.filteredOptions = this.allOptions
    this.renderOptions()
  }

  renderOptions() {
    this.optionsContainer.innerHTML = ''

    if (this.filteredOptions.length === 0) {
      const noResults = document.createElement('div')
      noResults.className = 'vanilla-combobox-option no-results'
      noResults.textContent = 'Aucun résultat'
      this.optionsContainer.appendChild(noResults)
      return
    }

    this.filteredOptions.forEach((option, index) => {
      if (option.value === '' && !this.options.allowClear) return

      const optionEl = document.createElement('div')
      optionEl.className = 'vanilla-combobox-option'
      optionEl.setAttribute('role', 'option')
      optionEl.setAttribute('data-value', option.value)
      optionEl.textContent = option.text

      if (option.disabled) {
        optionEl.classList.add('disabled')
      }

      if (option.value === this.select.value) {
        optionEl.classList.add('selected')
        optionEl.setAttribute('aria-selected', 'true')
      }

      optionEl.addEventListener('click', (e) => {
        e.stopPropagation()
        if (!option.disabled) {
          this.selectOption(option.value)
        }
      })

      this.optionsContainer.appendChild(optionEl)
    })
  }

  filterOptions(searchTerm) {
    if (!searchTerm) {
      this.filteredOptions = this.allOptions
    } else {
      const term = searchTerm.toLowerCase()
      this.filteredOptions = this.allOptions.filter(opt =>
        opt.text.toLowerCase().includes(term)
      )
    }
    this.renderOptions()
  }

  selectOption(value) {
    this.select.value = value
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateOptions()
    this.close()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.container.classList.add('open')
    this.display.setAttribute('aria-expanded', 'true')

    if (this.searchInput) {
      this.searchInput.value = ''
      setTimeout(() => this.searchInput.focus(), 10)
    }

    // Position dropdown
    this.positionDropdown()
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.container.classList.remove('open')
    this.display.setAttribute('aria-expanded', 'false')

    if (this.searchInput) {
      this.searchInput.value = ''
      this.filteredOptions = this.allOptions
      this.renderOptions()
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  positionDropdown() {
    const rect = this.container.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    if (spaceBelow < 200 && spaceAbove > spaceBelow) {
      this.dropdown.classList.add('open-up')
    } else {
      this.dropdown.classList.remove('open-up')
    }
  }

  bindEvents() {
    // Display click
    this.display.addEventListener('click', (e) => {
      e.stopPropagation()
      this.toggle()
    })

    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.filterOptions(e.target.value)
      })

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close()
        } else if (e.key === 'Enter') {
          e.preventDefault()
          const firstOption = this.optionsContainer.querySelector('.vanilla-combobox-option:not(.disabled):not(.no-results)')
          if (firstOption) {
            firstOption.click()
          }
        } else if (e.key === 'ArrowDown') {
          e.preventDefault()
          this.focusNextOption(1)
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          this.focusNextOption(-1)
        }
      })
    }

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.close()
      }
    })

    // Keyboard navigation on display
    this.display.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.toggle()
      } else if (e.key === 'Escape') {
        this.close()
      }
    })

    // Update when select changes externally
    this.select.addEventListener('change', () => {
      this.updateOptions()
    })
  }

  focusNextOption(direction) {
    const options = Array.from(this.optionsContainer.querySelectorAll('.vanilla-combobox-option:not(.disabled):not(.no-results)'))
    const currentIndex = options.findIndex(opt => opt.classList.contains('focused'))
    let nextIndex = currentIndex + direction

    if (nextIndex < 0) nextIndex = options.length - 1
    if (nextIndex >= options.length) nextIndex = 0

    options.forEach(opt => opt.classList.remove('focused'))
    if (options[nextIndex]) {
      options[nextIndex].classList.add('focused')
      options[nextIndex].scrollIntoView({ block: 'nearest' })
    }
  }

  destroy() {
    if (this.wrapper && this.wrapper.parentNode) {
      this.select.style.display = ''
      this.wrapper.parentNode.insertBefore(this.select, this.wrapper)
      this.wrapper.remove()
    }
    delete this.select._vanillaCombobox
  }
}

// Initialize all comboboxes
function initializeComboboxes(container = document) {
  // Only initialize selects that don't have search-select-input class
  // (those are handled by SlimSelect)
  // Also skip multiple selects (handled by initializeMultipleSelects)
  const selects = container.querySelectorAll('select:not(.flatpickr-calendar select):not(.search-select-input):not([multiple]):not([data-combobox-disabled])')

  selects.forEach(select => {
    // Skip if already initialized or if it's a search_select
    if (select._vanillaCombobox || select.classList.contains('search-select-input')) return

    // Skip multiple selects (handled separately)
    if (select.hasAttribute('multiple')) return

    // Skip very small selects (like country codes with 2-3 options)
    if (select.options.length <= 3) return

    // Skip if explicitly disabled
    if (select.hasAttribute('data-combobox-disabled')) return

    new VanillaCombobox(select, {
      searchable: select.options.length > 10, // Auto-enable search for large lists
      allowClear: select.hasAttribute('data-allow-clear') ? select.getAttribute('data-allow-clear') !== 'false' : true
    })
  })
}

// Make it globally available
window.VanillaCombobox = VanillaCombobox
window.initializeComboboxes = initializeComboboxes

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeComboboxes()
  })
} else {
  initializeComboboxes()
}

